import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Truck, Download, Printer, RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtQty = (n) => (n == null ? "-" : Number(n).toLocaleString("en-ZA", { minimumFractionDigits: 0, maximumFractionDigits: 2 }));

export default function SalesBySupplierModal({ open, onOpenChange }) {
  const [suppliers, setSuppliers] = useState([]);
  const [supplier, setSupplier] = useState("");
  const [includeZero, setIncludeZero] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");

  // Load supplier list when modal opens.
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/suppliers`);
        const list = (r.data || []).map((s) => s.name).filter(Boolean).sort();
        setSuppliers(list);
      } catch (e) {
        console.error(e);
        toast.error("Failed to load suppliers");
      }
    })();
  }, [open]);

  const runReport = async () => {
    if (!supplier) {
      toast.error("Please choose a supplier first");
      return;
    }
    setLoading(true);
    setData(null);
    try {
      const r = await axios.get(`${API}/reports/sales/by-supplier`, {
        params: { supplier, include_zero: includeZero },
      });
      setData(r.data);
      if ((r.data.products || []).length === 0) {
        toast.info("No sales found for this supplier in the last 12 months");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to run report");
    } finally { setLoading(false); }
  };

  const downloadExcel = async () => {
    if (!supplier) return;
    setDownloading(true);
    try {
      const r = await axios.get(`${API}/reports/sales/by-supplier/export`, {
        params: { supplier, include_zero: includeZero },
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sales-by-supplier-${supplier.replace(/[^a-z0-9-]+/gi, "_")}-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch (e) {
      toast.error("Excel download failed");
    } finally { setDownloading(false); }
  };

  const printReport = () => {
    if (!data) return;
    const rows = filteredProducts;
    const p = data.periods;
    const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
    const w = window.open("", "_blank", "width=1024,height=768");
    if (!w) {
      toast.error("Pop-up blocked. Please allow pop-ups for printing.");
      return;
    }
    const style = `
      <style>
        @page { size: A4 landscape; margin: 12mm; }
        body { font-family: Arial, sans-serif; font-size: 11px; color: #000; }
        h1 { font-size: 18px; margin: 0 0 4px; }
        h2 { font-size: 12px; margin: 0 0 12px; color: #444; font-weight: normal; }
        table { border-collapse: collapse; width: 100%; }
        th, td { border: 1px solid #000; padding: 4px 6px; }
        th { background: #1F4E79; color: #fff; text-align: left; font-size: 11px; }
        td.num { text-align: right; font-variant-numeric: tabular-nums; }
        tr:nth-child(even) td { background: #f5f5f5; }
        .meta { font-size: 10px; color: #666; margin-top: 6px; }
        @media print { .no-print { display: none } }
      </style>`;
    const rowsHtml = rows.map((row) => `
      <tr>
        <td>${esc(row.plu_code)}</td>
        <td>${esc(row.name)}</td>
        <td class="num">${fmtQty(row.current_stock)}</td>
        <td class="num">${fmtQty(row.this_month)}</td>
        <td class="num">${fmtQty(row.last_month)}</td>
        <td class="num">${fmtQty(row.last_3_months)}</td>
        <td class="num">${fmtQty(row.last_6_months)}</td>
        <td class="num">${fmtQty(row.last_12_months)}</td>
      </tr>`).join("");
    w.document.write(`<!DOCTYPE html><html><head><title>Sales by Supplier — ${esc(supplier)}</title>${style}</head><body>
      <h1>Sales Report — ${esc(supplier)}</h1>
      <h2>This month: ${esc(p.this_month.label)} • Last month: ${esc(p.last_month.label)} • 3M/6M/12M are rolling from today</h2>
      <table>
        <thead><tr>
          <th>PLU</th><th>Description</th><th>Stock</th>
          <th>This Month</th><th>Last Month</th>
          <th>Last 3 Months</th><th>Last 6 Months</th><th>Last 12 Months</th>
        </tr></thead>
        <tbody>${rowsHtml}</tbody>
      </table>
      <div class="meta">Generated ${new Date().toLocaleString("en-ZA")} — ${rows.length} products</div>
      <script>window.onload = () => { window.print(); setTimeout(() => window.close(), 500); };</script>
    </body></html>`);
    w.document.close();
  };

  const filteredProducts = useMemo(() => {
    const rows = data?.products || [];
    if (!filter) return rows;
    const f = filter.toLowerCase();
    return rows.filter((r) =>
      (r.plu_code || "").toLowerCase().includes(f) ||
      (r.name || "").toLowerCase().includes(f)
    );
  }, [data, filter]);

  const totals = useMemo(() => {
    const t = { this_month: 0, last_month: 0, last_3_months: 0, last_6_months: 0, last_12_months: 0 };
    for (const r of filteredProducts) {
      t.this_month += r.this_month;
      t.last_month += r.last_month;
      t.last_3_months += r.last_3_months;
      t.last_6_months += r.last_6_months;
      t.last_12_months += r.last_12_months;
    }
    return t;
  }, [filteredProducts]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="sales-by-supplier-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5 text-blue-600" />
            Sales Report per Supplier
          </DialogTitle>
          <DialogDescription>
            Quantity sold per product across five periods. Pick a supplier and run the report.
          </DialogDescription>
        </DialogHeader>

        {/* Controls */}
        <div className="flex flex-wrap items-end gap-3 py-2 border-b">
          <div className="min-w-[260px]">
            <label className="text-xs text-slate-500">Supplier</label>
            <Select value={supplier || undefined} onValueChange={setSupplier}>
              <SelectTrigger data-testid="supplier-select"><SelectValue placeholder="Choose supplier…" /></SelectTrigger>
              <SelectContent className="max-h-96">
                {suppliers.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox
              checked={includeZero}
              onCheckedChange={setIncludeZero}
              data-testid="include-zero-check"
            />
            Include products with zero sales
          </label>
          <Button onClick={runReport} disabled={loading || !supplier} data-testid="run-report-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Run Report
          </Button>
          <div className="flex-1" />
          <Button
            variant="outline"
            onClick={downloadExcel}
            disabled={!data || downloading || !supplier}
            data-testid="download-excel-btn"
          >
            <Download className={`h-4 w-4 mr-2 ${downloading ? "animate-spin" : ""}`} /> Download Excel
          </Button>
          <Button
            variant="outline"
            onClick={printReport}
            disabled={!data || (data.products || []).length === 0}
            data-testid="print-report-btn"
          >
            <Printer className="h-4 w-4 mr-2" /> Print
          </Button>
        </div>

        {/* Data */}
        {data && (
          <>
            <div className="flex items-center gap-2 py-2">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Filter results (PLU or name)…"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="pl-8"
                  data-testid="results-filter"
                />
              </div>
              <Badge variant="secondary" data-testid="results-count">{filteredProducts.length} of {(data.products || []).length} products</Badge>
              <span className="text-xs text-slate-500">
                {data.periods.this_month.label} • {data.periods.last_month.label} • 3M/6M/12M rolling
              </span>
            </div>

            <div className="overflow-auto border rounded-md flex-1">
              <table className="w-full text-sm" data-testid="report-table">
                <thead className="bg-slate-900 text-white text-xs uppercase sticky top-0">
                  <tr>
                    <th className="p-2 text-left">PLU</th>
                    <th className="p-2 text-left">Description</th>
                    <th className="p-2 text-right">Stock</th>
                    <th className="p-2 text-right">This Month</th>
                    <th className="p-2 text-right">Last Month</th>
                    <th className="p-2 text-right">Last 3 Months</th>
                    <th className="p-2 text-right">Last 6 Months</th>
                    <th className="p-2 text-right">Last 12 Months</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.length === 0 && (
                    <tr><td colSpan={8} className="p-8 text-center text-slate-500">No products to display.</td></tr>
                  )}
                  {filteredProducts.map((r) => (
                    <tr key={r.plu_code} className="border-t hover:bg-slate-50" data-testid={`row-${r.plu_code}`}>
                      <td className="p-2 font-mono text-xs">{r.plu_code}</td>
                      <td className="p-2">{r.name}</td>
                      <td className="p-2 text-right">{fmtQty(r.current_stock)}</td>
                      <td className="p-2 text-right">{fmtQty(r.this_month)}</td>
                      <td className="p-2 text-right">{fmtQty(r.last_month)}</td>
                      <td className="p-2 text-right">{fmtQty(r.last_3_months)}</td>
                      <td className="p-2 text-right">{fmtQty(r.last_6_months)}</td>
                      <td className="p-2 text-right font-medium">{fmtQty(r.last_12_months)}</td>
                    </tr>
                  ))}
                </tbody>
                {filteredProducts.length > 0 && (
                  <tfoot className="bg-slate-100 font-semibold sticky bottom-0">
                    <tr>
                      <td colSpan={3} className="p-2 text-right">Totals</td>
                      <td className="p-2 text-right" data-testid="total-this-month">{fmtQty(totals.this_month)}</td>
                      <td className="p-2 text-right" data-testid="total-last-month">{fmtQty(totals.last_month)}</td>
                      <td className="p-2 text-right" data-testid="total-3m">{fmtQty(totals.last_3_months)}</td>
                      <td className="p-2 text-right" data-testid="total-6m">{fmtQty(totals.last_6_months)}</td>
                      <td className="p-2 text-right" data-testid="total-12m">{fmtQty(totals.last_12_months)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </>
        )}

        {!data && !loading && (
          <div className="flex flex-col items-center justify-center py-16 text-slate-400">
            <Truck className="h-10 w-10 mb-2" />
            <p className="text-sm">Choose a supplier and click <strong>Run Report</strong> to see the sales breakdown.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
